import React, { useEffect, useState, useCallback } from "react";
import { Trophy } from "lucide-react";
import api from "../../api";
import { useLang } from "../../i18n";
import { useAuth } from "../../context/AuthContext";
import MagicalCard from "../../components/MagicalCard";
import LeaderboardPanel from "../../components/LeaderboardPanel";
import LevelPath from "../../components/LevelPath";
import ProfileCard from "../../components/ProfileCard";
import EarnPointsPanel from "../../components/EarnPointsPanel";
import BadgesGrid from "../../components/BadgesGrid";
import AchievementsPanel from "../../components/AchievementsPanel";
import WeeklyChallengeCard from "../../components/WeeklyChallengeCard";
import LevelUpCelebration from "../../components/LevelUpCelebration";
import { SkeletonPanel } from "../../components/common";

export default function StudentDashboard() {
  const { t, pick, dir } = useLang();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [celebrateNotif, setCelebrateNotif] = useState(null);

  const load = useCallback(async () => {
    setError(false);
    try {
      const { data } = await api.get("/dashboard");
      setData(data);
      const n = await api.get("/notifications");
      const lv = n.data.notifications.find((x) => x.type === "level_up" && !x.read);
      if (lv) setCelebrateNotif(lv);
    } catch (e) {
      setError(true);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (error) {
    return (
      <div className="text-center py-20">
        <p className="font-bold mb-4">{t("common.error_generic")}</p>
        <button data-testid="dash-retry" onClick={load} className="btn-magic">{t("common.retry")}</button>
      </div>
    );
  }
  if (!data) return <div className="grid gap-6 md:grid-cols-3"><SkeletonPanel lines={5} /><SkeletonPanel lines={6} /><SkeletonPanel lines={5} /></div>;

  const activeChallenge = data.challenges.find((c) => ["active", "awaiting", "completed"].includes(c.status));
  const cell = (pos, children, key) => (
    <div key={key} dir={dir} className={pos}>{children}</div>
  );

  return (
    <div data-testid="student-dashboard">
      <p className="text-center text-lg font-extrabold text-[#D8D4F2] mb-6">
        {t("dash.welcome_back", { name: pick(user, "name") })}
      </p>
      <div dir="ltr" className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-5 mt-8">
        {cell("md:col-start-10 md:col-span-3 md:row-start-1 md:row-span-2",
          <ProfileCard student={data.student} totalPoints={data.total_points} level={data.level}
            rank={data.rank} badgeCount={data.badge_count} nearThreshold={data.near_level_threshold} />, "profile")}
        {cell("md:col-start-4 md:col-span-6 md:row-start-1",
          <MagicalCard title={t("dash.excellence_stages")} testId="stages-card">
            <LevelPath levels={data.levels} currentPoints={data.total_points} />
            <p className="text-center text-sm text-[#D8D4F2] font-bold mt-2">{t("dash.collect_points")}</p>
          </MagicalCard>, "stages")}
        {cell("md:col-start-4 md:col-span-6 md:row-start-4 md:row-span-2",
          <div className="space-y-5 h-full flex flex-col gap-5">
            <WeeklyChallengeCard challenge={activeChallenge} onCompleted={load} isStudent />
            <MagicalCard title={t("dash.term_goal")} variant="ribbon-gold" testId="term-goal-card" className="flex-1">
              <div className="flex items-center justify-center gap-3 py-2">
                <Trophy className="w-10 h-10 text-[#FFC83D] animate-floaty" fill="currentColor" style={{ filter: "drop-shadow(0 0 12px rgba(255,200,61,0.6))" }} />
                <p className="text-xl font-black text-[#FFC83D]">{pick(data.settings, "term_goal")}</p>
              </div>
            </MagicalCard>
          </div>, "challenge")}
        {cell("md:col-start-1 md:col-span-3 md:row-start-1 md:row-span-3",
          <LeaderboardPanel entries={data.weekly_leaders} />, "leaders")}
        {cell("md:col-start-4 md:col-span-6 md:row-start-2 md:row-span-2",
          <EarnPointsPanel categories={data.categories} />, "earn")}
        {cell("md:col-start-1 md:col-span-3 md:row-start-4 md:row-span-2",
          <BadgesGrid badges={data.badges} />, "badges")}
        {cell("md:col-start-10 md:col-span-3 md:row-start-3 md:row-span-3",
          <AchievementsPanel achievements={data.achievements} categories={data.categories} />, "achievements")}
      </div>
      <LevelUpCelebration
        open={!!celebrateNotif}
        studentName={pick(user, "name")}
        level={data.level.current}
        onClose={async () => {
          if (celebrateNotif) await api.post(`/notifications/${celebrateNotif.id}/read`).catch(() => {});
          setCelebrateNotif(null);
        }}
      />
    </div>
  );
}
